import React, { useState, useEffect } from 'react';
import { Clock, Search, Camera, OctagonAlert, Wrench } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import EquipmentDetailsModal from 'components/cpEquipments/EquipmentDetailsModal';
import { getCrewEquipment } from 'services/creatorService';
import EquipmentRentCard from 'components/cpEquipments/equipmentRentcard';

const statCardClass =
  "bg-white p-5 rounded-xl shadow-sm border border-gray-100 flex items-start justify-between relative overflow-hidden group " +
  "hover:shadow-md hover:border-[#C4B5A3] transition-all duration-300";

const EquipmentRentPage = () => {
  const [equipmentDetailsOpen, setEquipmentDetailsOpen] = useState(false);
  const [equipmentDetailsData, setEquipmentDetailsData] = useState(null);
  const [search, setSearch] = useState('');
  const [type, setType] = useState('all');
  const [status, setStatus] = useState('all');
  const [equipments, setEquipments] = useState([]);
  const [activeTab, setActiveTab] = useState("rent");

  const handleOpenEquipmentDetails = (equipmentId) => {
    const item = equipments.find((item) => item.equipment?.equipment_id === equipmentId);
    setEquipmentDetailsData(item?.equipment || null);
    setEquipmentDetailsOpen(true);
  };

  useEffect(() => {
    getCrewEquipment({})
      .then(response => {
        if (response?.data) {
          setEquipments(response.data);
        }
      })
      .catch(error => {
        console.error('Error fetching equipment data:', error);
      });
  }, []);

  // Safe filter logic
  const filteredCards = equipments.filter((c) => {
    const equipmentName = c.equipment?.equipment_name || c.equipment_name || "";
    const matchesSearch = equipmentName
      .toLowerCase()
      .includes(search.trim().toLowerCase());

    const matchesStatus = status === 'all'
      ? true
      : String(c.is_available_for_rent) === status || (status === '1' && c.is_available_for_rent === true);

    return matchesSearch && matchesStatus;
  });

  const stats = [
    { title: 'Total Equipments', value: equipments.length, icon: Camera },
    { title: 'Pending Requests', value: 5, icon: Clock },
    { title: 'Alerts', value: 2, icon: OctagonAlert },
    { title: 'Under Maintenance', value: 0, icon: Wrench },
  ];

  return (
    <div className="p-6">
      <div className="bg-[#F5F1EE] rounded-2xl border border-[#E8DDD3]">
        {/* Header Section */}
        <div className="p-6 pb-3 border-b border-[#E8DDD3]">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {activeTab === "rent" ? "Rent Equipment" : "Rented Equipment"}
              </h1>
              <p className="text-gray-500 text-sm mt-1">
                {activeTab === "rent"
                  ? "Discover and rent professional gear for your next shoot."
                  : "Track your active rentals and manage equipment deliveries."}
              </p>
            </div>
          </div>

          {/* STATS CARDS - Now only shows if activeTab is "rented" */}
          {activeTab === "rented" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8 animate-in fade-in slide-in-from-top-4 duration-300">
              {stats.map((stat, idx) => (
                <div key={idx} className={statCardClass}>
                  <div>
                    <p className="text-sm text-gray-500 font-medium">{stat.title}</p>
                    <h3 className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</h3>
                  </div>
                  <div className="p-2 bg-[#F5F1EE] rounded-lg">
                    <stat.icon className="h-5 w-5 text-[#C4B5A3]" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Tab Container */}
          <div className="inline-flex w-[300px] bg-[#ECE1CE] rounded-xl p-1 border border-[#E8DDD3]">
            <button
              onClick={() => setActiveTab("rent")}
              className={`px-8 py-2 rounded-lg text-sm font-medium transition-all
                ${activeTab === "rent" ? "bg-white text-[#111827] shadow-sm" : "text-[#6B7280] hover:text-[#111827]"}`}
            >
              Rent Equipment
            </button>
            <button
              onClick={() => setActiveTab("rented")}
              className={`px-8 py-2 rounded-lg text-sm font-medium transition-all
                ${activeTab === "rented" ? "bg-white text-[#111827] shadow-sm" : "text-[#6B7280] hover:text-[#111827]"}`}
            >
              Rented
            </button>
          </div>

          {/* Search and Filters */}
          <div className="mt-4 flex flex-col sm:flex-row justify-between items-center gap-3">
            <div className="relative w-full sm:max-w-[260px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search equipment..."
                className="pl-10 h-10 rounded-lg bg-white border border-[#E8DDD3] shadow-sm"
              />
            </div>

            <div className="flex gap-2">
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className="h-10 w-[160px] rounded-lg bg-white border border-[#E8DDD3] shadow-sm">
                  <SelectValue placeholder="All Equipments" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Equipments</SelectItem>
                  <SelectItem value="shoot_request">Shoot Request</SelectItem>
                </SelectContent>
              </Select>

              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="h-10 w-[140px] rounded-lg bg-white border border-[#E8DDD3] shadow-sm">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="1">Available</SelectItem>
                  <SelectItem value="2">On Rent</SelectItem>
                  <SelectItem value="4">Under Maintenance</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="p-6 pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredCards.length > 0 ? (
              filteredCards.map((item, index) => (
                <EquipmentRentCard
                  key={item.equipment?.equipment_id || index}
                  item={item}
                  handleOpenEquipmentDetails={handleOpenEquipmentDetails}
                />
              ))
            ) : (
              <div className="col-span-full py-10 text-center text-gray-400 italic">
                No matching equipment found.
              </div>
            )}
          </div>
        </div>
      </div>

      <EquipmentDetailsModal
        open={equipmentDetailsOpen}
        onOpenChange={setEquipmentDetailsOpen}
        equipment={equipmentDetailsData}
      />
    </div>
  );
};

export default EquipmentRentPage;
